import React from "react";
import { Flame, Thermometer, Snowflake, XCircle, ArrowUpRight, ArrowDownLeft, ArrowLeftRight } from "lucide-react";

export function CategoryBadge({ category, className = "" }) {
  const map = {
    HOT: { c: "bg-red-100 text-red-700 border-red-200 dark:bg-red-500/15 dark:text-red-400", Icon: Flame },
    WARM: { c: "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-500/15 dark:text-amber-400", Icon: Thermometer },
    COLD: { c: "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-500/15 dark:text-blue-400", Icon: Snowflake },
    "Not Interested": { c: "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-500/15 dark:text-slate-400", Icon: XCircle },
    DISQUALIFIED: { c: "bg-slate-200 text-slate-600 border-slate-300 dark:bg-slate-600/25 dark:text-slate-400", Icon: XCircle },
  };
  const { c, Icon } = map[category] || map.COLD;
  return (
    <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-semibold ${c} ${className}`} data-testid={`category-badge-${category}`}>
      <Icon className="w-3 h-3" /> {category}
    </span>
  );
}

export function scoreCategory(score) {
  if (score >= 80) return "HOT";
  if (score >= 50) return "WARM";
  return "COLD";
}

export function ScoreBadge({ score }) {
  const cat = scoreCategory(score);
  const color = score >= 80 ? "text-red-600 bg-red-50 dark:bg-red-500/10" : score >= 50 ? "text-amber-600 bg-amber-50 dark:bg-amber-500/10" : score >= 1 ? "text-blue-600 bg-blue-50 dark:bg-blue-500/10" : "text-slate-500 bg-slate-100 dark:bg-slate-500/10";
  return <span className={`inline-flex items-center justify-center rounded-md px-2 py-0.5 text-xs font-bold font-display ${color}`}>{score}</span>;
}

export function DirectionBadge({ direction }) {
  const map = {
    outbound: { c: "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-500/15 dark:text-blue-400", Icon: ArrowUpRight, label: "Outbound" },
    inbound: { c: "bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-500/15 dark:text-purple-400", Icon: ArrowDownLeft, label: "Inbound" },
    both: { c: "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-500/15 dark:text-slate-300", Icon: ArrowLeftRight, label: "Both" },
  };
  const { c, Icon, label } = map[direction] || map.outbound;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${c}`}>
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}

export function StatusPill({ status }) {
  const map = {
    Pending: "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300",
    Calling: "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-400",
    "New Inbound": "bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-400",
    Qualified: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400",
    "Demo Booked": "bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-400",
    Converted: "bg-teal-100 text-teal-700 dark:bg-teal-500/15 dark:text-teal-400",
    "Not Interested": "bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400",
    "No Answer": "bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-400",
    DNC: "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400",
  };
  return <span className={`inline-flex rounded-md px-2 py-0.5 text-xs font-medium ${map[status] || map.Pending}`}>{status}</span>;
}

export function LiveDot() {
  return (
    <span className="relative flex h-2.5 w-2.5">
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
    </span>
  );
}

export function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export function fmtDur(secs) {
  if (!secs) return "0s";
  const m = Math.floor(secs / 60), s = secs % 60;
  return m ? `${m}m ${s}s` : `${s}s`;
}

export const LANG_NAMES = {
  "hi-IN": "Hindi", "gu-IN": "Gujarati", "ta-IN": "Tamil", "te-IN": "Telugu", "kn-IN": "Kannada", "mr-IN": "Marathi", auto: "Auto",
};
